import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector } from "react-redux";
import { format } from "date-fns";
import toast from 'react-hot-toast';
import {
  useMediaQuery,
  useTheme,
  Box,
  Typography,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import { DragIndicator } from '@mui/icons-material';
import { LAYOUT_HEIGHT } from 'state/constants';
import api from 'state/axios';
import { filterCrewMember } from 'state/helpers';
import { countAllCrewMembers } from 'scenes/charts/utils';
import useAuthCheck from 'hooks/useAuthCheck';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import FilterButton from 'components/FilterButton';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import Header from 'components/Header';
import StatBox from 'components/StatBox';
import ScrollToTop from 'components/ScrollToTop';
import PageFooter from 'components/PageFooter';
import SortBarChart from 'components/chartNivo/SortBarChart';
import ChartDialog from 'components/chartNivo/ChartDialog';
import CrewFilterDialog from 'components/CrewFilterDialog';
import { boxStyles as styles } from 'components/CustomStyles';
import CrewStats1 from './CrewStats1';
import CrewStats2 from './CrewStats2';
import AircraftUtilization from './AircraftUtilization';
import CrewResourceTable from './CrewResourceTable';



/* ===== Helpers ===== */
const safeObject = v => (v && typeof v === 'object' ? v : {});
const safeNumber = v => (typeof v === 'number' ? v : 0);
const safeDigits = (v, min = 0, max = 2) => Number.isFinite(Number(v))
  ? Number(v).toLocaleString('en-US', {
      minimumFractionDigits: min,
      maximumFractionDigits: max,
    })
  : '0';

const viewOptions = [
  { value: "crewByPostBase", label: "by Post & Base" },
  { value: "crewByPost", label: "by Post" },
  { value: "crewByBase", label: "by Base" },
];

const defaultFilters = { isActive: 'ALL' };


const CrewDashboard = () => {
  useAuthCheck();
  const timeMode = useSelector((state) => state.timemode);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  const { startDate, endDate } = useDateRangeContext();

  const scrollRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [chartOpen, setChartOpen] = useState(false);
  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
  const [filters, setFilters] = useState({ isActive: 'ALL' });
  const [view, setView] = useState('crewByPostBase');
  const [crewStats, setCrewStats] = useState({});
  const crewRanksData = useSelector((state) => state.crewRanks);


  const fetchCrewStats = useCallback(async () => {
    if (!startDate || !endDate) return;

    const url = '/rosterdetails/crewstats';
    const payload = { startDate, endDate, timeMode };
    const res = await api.post(url, payload);

    setCrewStats(res.data);
  }, [timeMode, startDate, endDate]);


  useEffect(() => {
    const loadData = async () => {
      try {
        setError(null);
        setLoading(true);

        await fetchCrewStats();

      } catch (error) {
        setError(error);
        toast.error(error.response?.data?.message || "Failed to fetch data!");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [fetchCrewStats]);

  const filterCrewMembers = useCallback((members) => {
    if (!Array.isArray(members)) return [];
    if (!filters) return members;
    return members.filter(member => filterCrewMember(member, filters));
  }, [filters]);

  const data = useMemo(() => {
    const members = filterCrewMembers(crewStats.data?.rosteredCrewMembers || []);
    return {
      crewByPostBase: countAllCrewMembers(members, 'postbase', 'stackedbar'),
      crewByPost: countAllCrewMembers(members, 'post', 'stackedbar'),
      crewByBase: countAllCrewMembers(members, 'base', 'stackedbar'),
    };
  }, [crewStats, filterCrewMembers]);

  const handleViewChange = useCallback((e) => {
    setView(e.target.value);
  }, []);

  const handleOpen = useCallback(() => {
    setChartOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setChartOpen(false);
  }, []);

  const handleFilterOpen = () => setIsFilterDialogOpen(true);
  const handleFilterClose = () => setIsFilterDialogOpen(false);

  const handleFilterApply = (filterValues) => {
    setFilters(filterValues);
  };

  const {
    baseOptions,
    postOptions,
    trainingQualOptions,
  } = useMemo(() => {
    const rosteredCrewMembers = crewStats.data?.rosteredCrewMembers || [];
    if (!Array.isArray(rosteredCrewMembers) || rosteredCrewMembers.length === 0) {
      return { baseOptions: [], postOptions: [], trainingQualOptions: [] };
    }

    const baseSet = new Set();
    const postSet = new Set();
    const trainingQualSet = new Set();

    rosteredCrewMembers.forEach(m => {
      if (m.base) baseSet.add(m.base);
      if (m.post) postSet.add(m.post);
      m.trainingQuals?.forEach(q => q && trainingQualSet.add(q));
    });

    const rankMap = new Map(
      (crewRanksData || []).map(r => [r.rankCode, r])
    );

    return {
      baseOptions: [...baseSet].sort(),
      postOptions: [...postSet]
        .map(post => rankMap.get(post))
        .filter(Boolean)
        .sort((a, b) => a.rankPriority - b.rankPriority)
        .map(r => r.rankCode),
      trainingQualOptions: [...trainingQualSet].sort(),
    };
  }, [crewStats, crewRanksData]);


  const period = `${startDate ? format(startDate, 'ddMMMyy') : ''} - ${endDate ? format(endDate, 'ddMMMyy') : ''}`;
  const title = `All & Flying Crew ${period}`;

  const chartMap = useMemo(() => ({
    crewByPostBase: {
      component: SortBarChart,
      props: {
        title,
        bottomLegend: "Post & Base",
        leftLegend: "Count",
        isShowAvgLine: false,
        isSortVisible: false,
        isHorizontalBottomLegend: false,
        bottomOffSet: -45,
        bottomLegendOffSet: 5,
      },
    },
    crewByPost: {
      component: SortBarChart,
      props: {
        title,
        bottomLegend: "Post",
        leftLegend: "Count",
        isShowAvgLine: false,
        isSortVisible: false,
        isHorizontalBottomLegend: true,
        bottomOffSet: -90,
        bottomLegendOffSet: -40,
      },
    },
    crewByBase: {
      component: SortBarChart,
      props: {
        title,
        bottomLegend: "Base",
        leftLegend: "Count",
        isShowAvgLine: false,
        isSortVisible: false,
        isHorizontalBottomLegend: false,
        bottomOffSet: -60,
        bottomLegendOffSet: -10,
      },
    },
  }), [title]);

  const renderChart = useCallback((view, styles, props) => {
    const chart = chartMap[view];
    if (!chart) {
      return (
        <Box pl={1} sx={styles}>
          <Typography>No charts available</Typography>
        </Box>
      );
    }

    if (!data[view] || data[view].length === 0) {
      return (
        <FlexCenter sx={styles}>
          <CircularProgress size={30} />
        </FlexCenter>
      );
    }

    const ChartComponent = chart.component;

    return (
      <Box sx={{ ...styles }}>
        <ChartComponent
          data={data[view]}
          {...chart.props}
          {...props}
        />
      </Box>
    );
  }, [data, chartMap]);


  const stats = useMemo(() => {
    const summary = safeObject(crewStats?.data?.summary || {});
    const flyingRate = summary.totalCrew > 0
      ? (summary.totalOperatingCrew / summary.totalCrew)
      : 0;
    const nonFlyingRate = summary.totalCrew > 0
      ? (summary.totalNonOperatingCrew / summary.totalCrew)
      : 0;

    const crewRate = summary.totalCabinCrew > 0
      ? (summary.totalFlightCrew / summary.totalCabinCrew)
      : 0;

    const fcFlyingRate = summary.totalFlightCrew > 0
      ? (summary.totalOperatingFlightCrew / summary.totalFlightCrew)
      : 0;

    const ccFlyingRate = summary.totalCabinCrew > 0
      ? (summary.totalOperatingCabinCrew / summary.totalCabinCrew)
      : 0;

    const icon = <DragIndicator sx={{ color: "green.dark", fontSize: 24 }} />;

    return [
      {
        icon,
        props: {
          title: "Total Crew",
          divisor: safeNumber(summary.totalCrew).toLocaleString(),
          dividend: "",
          progress: null,
          rate: "100%",
        }
      },
      {
        icon,
        props: {
          title: "Flying Crew",
          divisor: safeNumber(summary.totalOperatingCrew).toLocaleString(),
          dividend: safeNumber(summary.totalCrew).toLocaleString(),
          progress: flyingRate,
          rate: `${(flyingRate * 100).toFixed(1)}%`,
        }
      },
      {
        icon,
        props: {
          title: "Non-Flying Crew",
          divisor: safeNumber(summary.totalNonOperatingCrew).toLocaleString(),
          dividend: safeNumber(summary.totalCrew).toLocaleString(),
          progress: nonFlyingRate,
          rate: `${(nonFlyingRate * 100).toFixed(1)}%`,
        }
      },
      {
        icon,
        props: {
          title: "Total FC / CC",
          divisor: safeNumber(summary.totalFlightCrew).toLocaleString(),
          dividend: safeNumber(summary.totalCabinCrew).toLocaleString(),
          progress: crewRate,
          rate: `${crewRate.toFixed(2)}`,
        }
      },
      {
        icon,
        props: {
          title: "Flying FC",
          divisor: safeNumber(summary.totalOperatingFlightCrew).toLocaleString(),
          dividend: safeNumber(summary.totalFlightCrew).toLocaleString(),
          progress: fcFlyingRate,
          rate: `${(fcFlyingRate * 100).toFixed(1)}%`,
        }
      },
      {
        icon,
        props: {
          title: "Flying CC",
          divisor: safeNumber(summary.totalOperatingCabinCrew).toLocaleString(),
          dividend: safeNumber(summary.totalCabinCrew).toLocaleString(),
          progress: ccFlyingRate,
          rate: `${(ccFlyingRate * 100).toFixed(1)}%`,
        }
      },
      {
        icon,
        props: {
          title: "Crew BH",
          divisor: safeDigits(safeNumber(summary.totalBlockHours), 0, 0),
          dividend: null,
          progress: null,
          rate: null,
        }
      },
      {
        icon,
        props: {
          title: "BH / Crew",
          divisor: safeNumber(summary.averageBlockHours)?.toFixed(1),
          dividend: null,
          progress: null,
          rate: null,
        }
      },
      {
        icon,
        props: {
          title: "Deadheadings",
          divisor: safeNumber(summary.totalPax)?.toFixed(0),
          dividend: null,
          progress: null,
          rate: null,
        }
      },
      {
        icon,
        props: {
          title: "Resigned Crew",
          divisor: safeNumber(summary.totalResignCrew),
          dividend: null,
          progress: null,
          rate: null,
        }
      },
      {
        icon,
        props: {
          title: "Maternity Crew",
          divisor: safeNumber(summary.totalMaternityCrew),
          dividend: null,
          progress: null,
          rate: null,
        }
      },
      {
        icon,
        props: {
          title: "LWOP Crew",
          divisor: safeNumber(summary.totalLwopCrew),
          dividend: null,
          progress: null,
          rate: null,
        }
      },
    ]
  }, [crewStats]);


  return (
    <Box className="main-container-box">
      <FlexBetween>
        <Header
          title="Crew Board"
          subtitle={`${loading ? 'Loading...' : 'Welcome to the dashboard'}`}
        />
      </FlexBetween>
      {loading ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <CircularProgress />
        </FlexCenter>
      ) : error ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <Typography color="error">Error: {error?.message || ''}</Typography>
        </FlexCenter>
      ) : (
        <Box
          ref={scrollRef}
          height={LAYOUT_HEIGHT}
          display="flex"
          flexDirection="column"
          gap={2}
          sx={{ overflowY: 'auto', pr: !isMobile ? 0.5 : 0 }}
        >
          {/* ROW 1 */}
          <Box
            display="grid"
            gridTemplateColumns="repeat(12, minmax(0, 1fr))"
            gridAutoRows={100}
            gap={2}
            sx={{ "& > div": { gridColumn: isDesktop ? "span 2" : isTablet ? "span 4" : isMobile ? "span 6" : "span 4" } }}
          >
            {stats?.map((item, index) => (
              <StatBox
                key={index}
                icon={item.icon}
                props={item.props}
              />
            ))}
          </Box>

          {/* ROW 2 */}
          <Box
            display="grid"
            gridTemplateColumns="repeat(12, minmax(0, 1fr))"
            gridAutoRows={100}
            gap={2}
          >
            <Box
              display="flex"
              flexDirection="column"
              gridColumn={isDesktop ? "span 6" : "span 12"}
              gridRow="span 4"
              overflow="auto"
              sx={{
                borderRadius: 1,
                border: "1px solid",
                borderColor: "neutral.deep",
                backgroundColor: "background.default",
              }}
            >
              <CrewStats1 data={crewStats.data}/>
            </Box>

            <Box
              display="flex"
              flexDirection="column"
              gridColumn={isDesktop ? "span 6" : "span 12"}
              gridRow="span 4"
              overflow="auto"
              sx={{
                borderRadius: 1,
                border: "1px solid",
                borderColor: "neutral.deep",
                backgroundColor: "background.default",
              }}
            >
              <CrewStats2 data={crewStats.data}/>
            </Box>
          </Box>

          {/* ROW 3 */}
          <Box
            display="grid"
            gridTemplateColumns="repeat(12, minmax(0, 1fr))"
            gridAutoRows={100}
            gap={2}
          >
            <Box
              display="flex"
              flexDirection="column"
              gridColumn={isDesktop ? "span 6" : "span 12"}
              gridRow="span 3"
              overflow="auto"
              sx={{
                borderRadius: 1,
                border: "1px solid",
                borderColor: "neutral.deep",
                backgroundColor: "background.default",
              }}
            >
              <AircraftUtilization
                aircraftUtilization={crewStats.data?.aircraftUtilization}
                availableByRank={crewStats.data?.availableByRank}
              />
            </Box>

            <Box
              display="flex"
              flexDirection="column"
              gridColumn={isDesktop ? "span 6" : "span 12"}
              gridRow="span 3"
              overflow="auto"
              sx={{
                borderRadius: 1,
                border: "1px solid",
                borderColor: "neutral.deep",
                backgroundColor: "background.default",
              }}
            >
              <CrewResourceTable data={crewStats.data?.crewResource} />
            </Box>
          </Box>

          {/* ROW 4 */}
          <Box
            display="grid"
            gridTemplateColumns="repeat(12, minmax(0, 1fr))"
            gridAutoRows={100}
            gap={2}
          >
            <Box
              display="flex"
              flexDirection="column"
              gridColumn="span 12"
              gridRow="span 4"
              overflow="auto"
              sx={{
                borderRadius: 1,
                border: "1px solid",
                borderColor: "neutral.deep",
                backgroundColor: "background.default",
              }}
            >
              <Box mx={1} mt={1} mb={2}>
                <FlexBetween>
                  <FormControl size="small">
                    <InputLabel>View</InputLabel>
                    <Select
                      variant="outlined"
                      label="View"
                      value={view}
                      onChange={handleViewChange}
                    >
                      {viewOptions.map(({ value, label }) => (
                        <MenuItem key={value} value={value}>
                          {label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FilterButton
                    filters={filters}
                    defaultFilters={defaultFilters}
                    onClick={handleFilterOpen}
                  />
                </FlexBetween>
              </Box>

              {/* Chart */}
              <Box
                flex={1}
                overflow="hidden"
                onDoubleClick={handleOpen}
              >
                {renderChart(view, { height: "100%" }, {})}
              </Box>
            </Box>
          </Box>

          <PageFooter />
          <ScrollToTop scrollRef={scrollRef} />
        </Box>
      )}

      {chartOpen && (
        <ChartDialog
          open={chartOpen}
          onClose={handleClose}
          chartComponent={renderChart(view, {...styles, pt: 3}, {})}
        />
      )}

      {isFilterDialogOpen &&
        <CrewFilterDialog
          open={isFilterDialogOpen}
          onClose={handleFilterClose}
          onApply={handleFilterApply}
          postOptions={postOptions}
          baseOptions={baseOptions}
          trainingQualOptions={trainingQualOptions}
          currentFilters={filters}
        />
      }
    </Box>
  );
};

export default CrewDashboard;
